import React, { useEffect, useState } from "react";
import { Col, Form, InputNumber, Row, Tabs } from "antd";
import { useTranslation } from "react-i18next";
import GeneralSelect from "../../../../components/selects/general/GeneralSelect";
import { EntBasicHB } from "../../../../models/handBooks/basicHB";
import { EntInitialFilter } from "../../../../models/filters/initialFilter";
import { useLazyGetTransportAccessListQuery } from "../../../../redux/api/handBooksApi/organic/transportAccessApi";
import { useLazyGetServiceTypeListQuery } from "../../../../redux/api/handBooksApi/organic/serviceTypeApi";
import { EntLogisticsCenterCapacity } from "../../../../models/organic/logisticsCenter";
import { useLazyGetCertificateListQuery } from "../../../../redux/api/handBooksApi/organic/certificateApi";

interface IProps {
  items: EntLogisticsCenterCapacity[]|null;
  setItems: (profiles: EntLogisticsCenterCapacity[]) => void;
}

export const LogisticsCenterItems: React.FC<IProps> = ({ items, setItems }) => {
  const { t } = useTranslation();

  const [tabs, setTabs] = useState<EntLogisticsCenterCapacity[]>([]);
  const [activeKey, setActiveKey] = useState<number>(0);

  const generateUniqueKey = (): number => Date.now() + Math.floor(Math.random() * 1000);

  const createNewProfile = (): EntLogisticsCenterCapacity => ({
	key: generateUniqueKey(),
	id: null,
	storageAreaM2: null,
	dailyThroughputTon: null,
	coldStorageVolumeM3: null,
	transportAccessIds: null,
	certificationsIds: null,
	availableServicesIds: null,
  });

  useEffect(() => {
	if (items && items.length > 0) {
	  const initialized = items.map((p) => ({
		...p,
		key: p.key ?? generateUniqueKey(),
	  }));
	  setTabs(initialized);
	  setActiveKey(initialized[0].key!);
	} else {
	  const defaultTab = createNewProfile();
	  setTabs([defaultTab]);
	  setActiveKey(defaultTab.key!);
	}
  }, []);

  useEffect(() => {
	setItems(tabs);
  }, [tabs]);

  const handleAdd = () => {
	const newTab = createNewProfile();
	const updated = [...tabs, newTab];
	setTabs(updated);
	setActiveKey(newTab.key!);
  };

  const handleDelete = (key: number) => {
	const newTabs = tabs.filter((tab) => tab.key !== key);
	setTabs(newTabs);
	setItems(newTabs);
	if (newTabs.length > 0) {
	  setActiveKey(newTabs[0].key!);
	}
  };

  const handleChange = (index: number, field: keyof EntLogisticsCenterCapacity, value: any) => {
	const updated = [...tabs];
	updated[index] = { ...updated[index], [field]: value };
	setTabs(updated);
  };

  return (
	<Tabs
	  type="editable-card"
	  activeKey={ activeKey.toString() }
	  onChange={ (key) => setActiveKey(Number(key)) }
	  onEdit={ (key, action) => {
		if (action === "add") handleAdd();
		else if (action === "remove") handleDelete(Number(key));
	  } }
	  items={ tabs.map((profile, index) => ({
		key: profile.key!.toString(),
		label: `${ t("Вместимость") } #${ index + 1 }`,
		children: (
		  <Form layout="vertical">
			<Row gutter={ 16 } key="step1">
			  <Col className="gutter-row" span={ 12 }>
				<Form.Item label="Площадь складов м²">
				  <InputNumber
					style={ { width: "100%" } }
					min={ 0 }
					step={ 0.1 }
					placeholder="Введите площадь"
					value={ profile.storageAreaM2 ?? "" }
					onChange={ (value) =>
					  handleChange(index, "storageAreaM2", value ?? null)
					}
				  />
				</Form.Item>
				<Form.Item
				  label="Суточная пропускная способность (т)"
				>
				  <InputNumber
					style={ { width: "100%" } }
					min={ 0 }
					placeholder="Введите объем"
					value={ profile.dailyThroughputTon ?? "" }
					onChange={ (value) =>
					  handleChange(index, "dailyThroughputTon", value ?? null)
					}
				  />
				</Form.Item>
				<Form.Item
				  label="Объем холодного хранения м³"
				>
				  <InputNumber
					style={ { width: "100%" } }
					min={ 0 }
					placeholder="Введите объем"
					value={ profile.coldStorageVolumeM3 ?? "" }
					onChange={ (value) =>
					  handleChange(index, "coldStorageVolumeM3", value ?? null)
					}
				  />
				</Form.Item>
			  </Col>
			  <Col className="gutter-row" span={ 12 }>
				<Form.Item label="Доступность">
				  <GeneralSelect<EntBasicHB, EntInitialFilter>
					mode={ "multiple" }
					queryHook={ useLazyGetTransportAccessListQuery }
					initialFilter={ new EntInitialFilter() }
					valueMultiple={ profile.transportAccessIds || [] }
					onChangeMultiple={ (val) => handleChange(index, "transportAccessIds", val) }
				  />
				</Form.Item>
				<Form.Item label="Сертификаты">
				  <GeneralSelect<EntBasicHB, EntInitialFilter>
					mode={ "multiple" }
					queryHook={ useLazyGetCertificateListQuery }
					initialFilter={ new EntInitialFilter() }
					valueMultiple={ profile.certificationsIds || [] }
					onChangeMultiple={ (val) => handleChange(index, "certificationsIds", val) }
				  />
				</Form.Item>
				<Form.Item label="Услуги">
				  <GeneralSelect<EntBasicHB, EntInitialFilter>
					mode={ "multiple" }
					queryHook={ useLazyGetServiceTypeListQuery }
					initialFilter={ new EntInitialFilter() }
					valueMultiple={ profile.availableServicesIds || [] }
					onChangeMultiple={ (val) => handleChange(index, "availableServicesIds", val) }
				  />
				</Form.Item>
			  </Col>
			</Row>
		  </Form>
		),
	  })) }
	/>
  );
};
